import { FormControl, Validators } from '@angular/forms';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../openapi';
import { bothRequired, CustomValidator, fromToDate } from '../../../../../../common/forms/custom.validators';
import { calcDaysBetweenDates } from '../../../../../../services/helpers';
import { RoadControlsForm, RoadControlsFormValue } from '../../components/road-controls-set/road-controls.form';
import {
  ExposedMapDictionary,
  ExposedMapDictRate,
  getRatesAreaByCodes
} from '../../../../../../common/exposed-map/exposed-map.component';
import { environment } from '../../../../../../../environments/environment';

export function toStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): RoadOccupationStageFormValue {
  return {
    id: '',
    roadWidth: (v?.roadWidth || 0).toFixed(2).replace('.', ','),
    roadWidth2: (v?.roadWidth2 || 0).toFixed(2).replace('.', ','),
    roadWidth3: (v?.roadWidth3 || 0).toFixed(2).replace('.', ','),
    roadLength: (v?.roadLength || 0).toFixed(2).replace('.', ','),
    roadLength2: (v?.roadLength2 || 0).toFixed(2).replace('.', ','),
    roadLength3: (v?.roadLength3 || 0).toFixed(2).replace('.', ','),
    roadArea: (v?.roadArea || 0).toFixed(2).toString().replace('.', ','),
    roadArea2: (v?.roadArea2 || 0).toFixed(2).toString().replace('.', ','),
    roadArea3: (v?.roadArea3 || 0).toFixed(2).toString().replace('.', ','),
    sidewalkWidth: (v?.sidewalkWidth || 0).toFixed(2).replace('.', ','),
    sidewalkLength: (v?.sidewalkLength || 0).toFixed(2).replace('.', ','),
    sidewalkArea: (v?.sidewalkArea || 0).toFixed(2).replace('.', ','),
    otherElementsWidth: (v?.otherElementsWidth || 0).toFixed(2).replace('.', ','),
    otherElementsLength: (v?.otherElementsLength || 0).toFixed(2).replace('.', ','),
    otherElementsArea: (v?.otherElementsArea || 0).toFixed(2).replace('.', ','),
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,
    dayQuantity: v?.dayQuantity || 0,

    infrastructureTelecommunicationOnly: !!v?.infrastructureTelecommunicationOnly,

    geometry: v?.geometry || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || []
  };
}

export interface RoadOccupationStageFormValue extends RoadControlsFormValue {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number;

  infrastructureTelecommunicationOnly: boolean;

  geometry: string;

  parcels: ParcelAppEntityParcelWrite[];

  roadWidth: string;
  roadWidth2: string;
  roadWidth3: string;

  roadLength: string;
  roadLength2: string;
  roadLength3: string;

  roadArea?: string;
  roadArea2?: string;
  roadArea3?: string;
  sidewalkWidth: string;
  sidewalkLength: string;
  sidewalkArea?: string;
  otherElementsWidth: string;
  otherElementsLength: string;
  otherElementsArea?: string;
}

export class RoadOccupationStageForm extends RoadControlsForm {

  constructor() {
    super({
      id: new FormControl('', { nonNullable: true }),
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),

      infrastructureTelecommunicationOnly: new FormControl(false, { nonNullable: true }),

      geometry: new FormControl(null, environment.production ? Validators.required : []),

      roadWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadWidth2: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadWidth3: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadLength2: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadLength3: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      roadArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),
      roadArea2: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),
      roadArea3: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      sidewalkWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      sidewalkLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      sidewalkArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      otherElementsWidth: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      otherElementsLength: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      otherElementsArea: new FormControl(
        { value: '', disabled: true },
        {
          validators: [Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      })
    }, {
      validators: [
        bothRequired('roadWidth', 'roadLength'),
        bothRequired('roadWidth2', 'roadLength2'),
        bothRequired('roadWidth3', 'roadLength3'),
        bothRequired('sidewalkWidth', 'sidewalkLength'),
        bothRequired('shoulderWidth', 'shoulderLength'),
        bothRequired('greeneryWidth', 'greeneryLength'),
        bothRequired('otherElementsWidth', 'otherElementsLength'),
        fromToDate('startDate', 'endDate')
      ]
    });
  }

  public get infrastructureTelecommunicationOnly(): FormControl {
    return this.get('infrastructureTelecommunicationOnly') as FormControl;
  }

  public onLoadExposedMapDict(v: ExposedMapDictionary): void {

    const roadArea = getRatesAreaByCodes(v.rates, [253, 254, 255, 256]);

    const roadArea2Value = getRatesAreaByCodes(v.rates, [269, 270, 279, 272]);

    const roadArea3Value = getRatesAreaByCodes(v.rates, [267, 266, 275, 265]);

    const sidewalkArea = getRatesAreaByCodes(v.rates, [268, 276, 273, 278]);

    const otherElementsArea = getRatesAreaByCodes(v.rates, [271, 280, 281, 277]);


    if (this.infrastructureTelecommunicationOnly.value) {
      this.roadWidth.setValue('', { emitEvent: false });
      this.roadLength.setValue('', { emitEvent: false });

      this.clearRoadControls();
      this.clearRoad2Controls();
      this.clearRoad3Controls();
      this.clearSidewalkControls();
      this.clearOtherElementsControls();

      this.roadArea.setValue((roadArea2Value + roadArea3Value + sidewalkArea + otherElementsArea) || roadArea || '');
    } else {
      this.clearRoadControls();
      this.clearRoad2Controls();
      this.clearRoad3Controls();
      this.clearSidewalkControls();
      this.clearOtherElementsControls();

      if (roadArea2Value) {
        this.roadWidth2.setValue('', { emitEvent: false });
        this.roadLength2.setValue('', { emitEvent: false });
        this.roadArea2.setValue(roadArea2Value);
      }
      if (roadArea3Value) {
        this.roadWidth3.setValue('', { emitEvent: false });
        this.roadLength3.setValue('', { emitEvent: false });
        this.roadArea3.setValue(roadArea3Value);
      }

      if (sidewalkArea) {
        this.sidewalkWidth.setValue('', { emitEvent: false });
        this.sidewalkLength.setValue('', { emitEvent: false });
        this.sidewalkArea.setValue(sidewalkArea);
      }

      if (otherElementsArea) {
        this.otherElementsWidth.setValue('', { emitEvent: false });
        this.otherElementsLength.setValue('', { emitEvent: false });
        this.otherElementsArea.setValue(otherElementsArea);
      }
    }
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  private get roadWidth(): FormControl {
    return this.get('roadWidth') as FormControl;
  }

  private get roadWidth2(): FormControl {
    return this.get('roadWidth2') as FormControl;
  }

  private get roadWidth3(): FormControl {
    return this.get('roadWidth3') as FormControl;
  }

  private get roadLength(): FormControl {
    return this.get('roadLength') as FormControl;
  }

  private get roadLength2(): FormControl {
    return this.get('roadLength2') as FormControl;
  }

  private get roadLength3(): FormControl {
    return this.get('roadLength3') as FormControl;
  }

  private get roadArea(): FormControl {
    return this.get('roadArea') as FormControl;
  }

  private get roadArea2(): FormControl {
    return this.get('roadArea2') as FormControl;
  }

  private get roadArea3(): FormControl {
    return this.get('roadArea3') as FormControl;
  }

  private get sidewalkWidth(): FormControl {
    return this.get('sidewalkWidth') as FormControl;
  }

  private get sidewalkLength(): FormControl {
    return this.get('sidewalkLength') as FormControl;
  }

  private get sidewalkArea(): FormControl {
    return this.get('sidewalkArea') as FormControl;
  }

  private get otherElementsWidth(): FormControl {
    return this.get('otherElementsWidth') as FormControl;
  }

  private get otherElementsLength(): FormControl {
    return this.get('otherElementsLength') as FormControl;
  }

  private get otherElementsArea(): FormControl {
    return this.get('otherElementsArea') as FormControl;
  }

  public get canClearRoadControls(): boolean {
    return this.roadWidth.value || this.roadLength.value || this.roadArea.value;
  }

  public clearRoadControls(): void {
    this.roadWidth.setValue('', { emitEvent: false });
    this.roadLength.setValue('', { emitEvent: false });
    this.roadArea.setValue('', { emitEvent: false });
  }

  public get canClearRoad2Controls(): boolean {
    return this.roadLength2.value || this.roadWidth2.value || this.roadArea2.value;
  }

  public clearRoad2Controls(): void {
    this.roadWidth2.setValue('', { emitEvent: false });
    this.roadLength2.setValue('', { emitEvent: false });
    this.roadArea2.setValue('', { emitEvent: false });
  }

  public get canClearRoad3Controls(): boolean {
    return this.roadLength3.value || this.roadWidth3.value || this.roadArea3.value;
  }

  public clearRoad3Controls(): void {
    this.roadWidth3.setValue('', { emitEvent: false });
    this.roadLength3.setValue('', { emitEvent: false });
    this.roadArea3.setValue('', { emitEvent: false });
  }


  public get canClearOtherElementsControls(): boolean {
    return this.otherElementsWidth.value || this.otherElementsLength.value || this.otherElementsArea.value;
  }

  public clearOtherElementsControls(): void {
    this.otherElementsWidth.setValue('', { emitEvent: false });
    this.otherElementsLength.setValue('', { emitEvent: false });
    this.otherElementsArea.setValue('', { emitEvent: false });
  }

  public get canClearSidewalkControls(): boolean {
    return this.sidewalkWidth.value || this.sidewalkLength.value || this.sidewalkArea.value;
  }

  public clearSidewalkControls(): void {
    this.sidewalkWidth.setValue('', { emitEvent: false });
    this.sidewalkLength.setValue('', { emitEvent: false });
    this.sidewalkArea.setValue('', { emitEvent: false });
  }

  public calcRoadArea(): void {
    this.roadArea.setValue(
      this.calcArea(this.roadWidth.value, this.roadLength.value)
    );
  }

  public calcRoadArea2(): void {
    this.roadArea2.setValue(
      this.calcArea(this.roadWidth2.value, this.roadLength2.value)
    );
  }

  public calcRoadArea3(): void {
    this.roadArea3.setValue(
      this.calcArea(this.roadWidth3.value, this.roadLength3.value)
    );
  }

  public calcSidewalkArea(): void {
    this.sidewalkArea.setValue(
      this.calcArea(this.sidewalkWidth.value, this.sidewalkLength.value)
    );
  }

  public calcOtherElementsArea(): void {
    this.otherElementsArea.setValue(
      this.calcArea(
        this.otherElementsWidth.value,
        this.otherElementsLength.value
      )
    );
  }

  private calcArea(width?: string, length?: string): string {
    if (!width || !length) {
      return '';
    }
    width = (width || '0').replaceAll(',', '.').replaceAll(' ', '');
    length = (length || '0').replaceAll(',', '.').replaceAll(' ', '');
    return (Number(width) * Number(length)).toFixed(2).replaceAll('.', ',');
  }

  public override get valid(): boolean {
    return super.valid && (!!this.roadArea.value || !!this.roadArea2.value || !!this.roadArea3.value || !!this.otherElementsArea.value || !!this.sidewalkArea.value);
  }

  public get values(): RoadOccupationStageFormValue {
    return {
      id: this.value.id,
      startDate: this.value.startDate,
      endDate: this.value.endDate,
      dayQuantity: this.dayQuantity.value,

      infrastructureTelecommunicationOnly: this.infrastructureTelecommunicationOnly.value,

      geometry: this.geometry.value,

      roadName: this.roadName.value,
      roadNumber: this.roadNumber.value,
      street: this.value.street,

      parcels: this.parcels.value,

      roadWidth: this.value.roadWidth,
      roadWidth2: this.value.roadWidth2,
      roadWidth3: this.value.roadWidth3,
      roadLength: this.value.roadLength,
      roadLength2: this.value.roadLength2,
      roadLength3: this.value.roadLength3,
      roadArea: this.roadArea.value,
      roadArea2: this.roadArea2.value,
      roadArea3: this.roadArea3.value,
      sidewalkWidth: this.value.sidewalkWidth,
      sidewalkLength: this.value.sidewalkLength,
      sidewalkArea: this.sidewalkArea.value,
      otherElementsWidth: this.value.otherElementsWidth,
      otherElementsLength: this.value.otherElementsLength,
      otherElementsArea: this.otherElementsArea.value
    };
  }
}
